import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { LocalService } from '@nx-ratenow/core/data-access-local-storage';
import { UserFacade } from '@nx-ratenow/core/data-access-user';
import { environment } from '@nx-ratenow/core/environment';
import { RefreshTokenService } from '@nx-ratenow/core/feature-auth';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private readonly expirySkewMs = 10_000;

  constructor(
    private authService: RefreshTokenService,
    private readonly store: Store,
    private localStorageService: LocalService,
    private userFacade: UserFacade
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (this.isTokenExpired(request)) {
      return this.refreshAndRetry(request, next);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error?.status === 401 && error?.error?.error === 'invalid_token' && error instanceof HttpErrorResponse) {
          return this.refreshAndRetry(request, next);
        }
        return throwError(() => error);
      })
    );
  }

  private isTokenExpired(request: HttpRequest<any>): boolean {
    if (request.url.includes('oauth/token')) {
      return false;
    }

    const storedOauthInfo = this.localStorageService.getData('oauthInfo');
    if (!storedOauthInfo) {
      return false;
    }

    const oauthInfo = JSON.parse(storedOauthInfo);
    if (!oauthInfo?.access_token || !oauthInfo?.expires_at) {
      return false;
    }

    return Date.now() >= oauthInfo.expires_at - this.expirySkewMs;
  }

  refreshAndRetry(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const storageOauthInfo = JSON.parse(this.localStorageService.getData('oauthInfo'));

      if (!storageOauthInfo.refresh_token) {
        return this.handleRefreshFailure();
      }

      return this.authService.refreshToken(storageOauthInfo.refresh_token).pipe(
        switchMap((newOauthInfo: any) => {
          this.refreshTokenSubject.next(newOauthInfo.access_token);
          this.isRefreshing = false;
          return next.handle(this.addTokenHeader(request, newOauthInfo.access_token));
        }),
        catchError(() => this.handleRefreshFailure())
      );
    }

    if (request.url.includes('oauth/token') && request.body?.includes('grant_type=refresh_token')) {
      return this.handleRefreshFailure();
    }

    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addTokenHeader(request, token)))
    );
  }

  private handleRefreshFailure(): Observable<never> {
    this.isRefreshing = false;
    this.userFacade.deleteTokenInfo();
    this.redirectToLogin();
    return EMPTY;
  }

  private addTokenHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`)
    });
  }

  private redirectToLogin(): void {
    const urlLogin =
      environment.logIn +
      `?response_type=code&client_id=${environment.apiClientId}&scope=${environment.scope}&redirect_uri=` +
      encodeURIComponent(window.location.origin + '/auth');

    window.location.href = urlLogin;
  }
}
